import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GraduationCap, Loader2, Send, CheckCircle } from 'lucide-react';
import { useValidateJoinCode, useSubmitEnrollmentRequest } from '@/hooks/useSchoolApi';
import { useCreatePassportProfile, usePassportProfile, useUpdatePassportRoles } from '@/hooks/useConnectApi';
import { normalizeJoinCode } from '@/lib/joinCode';

interface JoinSchoolFlowProps {
  displayName: string;
  onComplete: () => void;
}

export function JoinSchoolFlow({ displayName, onComplete }: JoinSchoolFlowProps) {
  const [codeInput, setCodeInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const normalized = normalizeJoinCode(codeInput);
  const isCodeComplete = normalized.length === 9;

  const { data: validation, isLoading: validating } = useValidateJoinCode(codeInput);
  const { data: passportProfile } = usePassportProfile();
  const createPassport = useCreatePassportProfile();
  const updateRoles = useUpdatePassportRoles();
  const submitRequest = useSubmitEnrollmentRequest();

  const handleSubmit = async () => {
    // Ensure passport profile exists with student role
    if (!passportProfile) {
      await createPassport.mutateAsync({ display_name: displayName.trim(), roles: ['student'] });
    } else if (!passportProfile.roles?.includes('student')) {
      updateRoles.mutate([...(passportProfile.roles || []), 'student']);
    }

    submitRequest.mutate(
      { joinCode: normalized },
      {
        onSuccess: () => setSubmitted(true),
      }
    );
  };

  if (submitted) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-4">
          <CheckCircle className="h-12 w-12 text-primary mx-auto" />
          <h2 className="text-xl font-semibold">Request Submitted</h2>
          <p className="text-muted-foreground text-sm">
            Your enrollment request has been submitted — awaiting instructor approval.
          </p>
          <Button onClick={onComplete}>Continue to Passport</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <GraduationCap className="h-10 w-10 text-primary mx-auto mb-2" />
        <CardTitle>Join a School</CardTitle>
        <CardDescription>Enter the cohort join code provided by your instructor.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="e.g. ABCD-1234"
          value={codeInput}
          onChange={e => setCodeInput(e.target.value)}
          maxLength={10}
          className="font-mono tracking-wider text-center text-lg"
          autoFocus
        />

        {validating && isCodeComplete && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {isCodeComplete && !validating && !validation && (
          <p className="text-sm text-destructive text-center">Invalid or inactive join code.</p>
        )}

        {validation && (
          <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
            <p className="text-sm font-medium">{validation.school_name}</p>
            <p className="text-xs text-muted-foreground">
              {validation.program_name} — {validation.cohort_name}
            </p>
          </div>
        )}

        <Button
          className="w-full"
          disabled={!validation || submitRequest.isPending || createPassport.isPending}
          onClick={handleSubmit}
        >
          {(submitRequest.isPending || createPassport.isPending) ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-3.5 w-3.5 mr-1.5" />
          )}
          Request Enrollment
        </Button>
      </CardContent>
    </Card>
  );
}
